"use client";

import type { useChat } from "@ai-sdk/react";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { AlertCircleIcon, CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import { useParams } from "next/navigation";
import * as React from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { AgentUIMessage } from "@/lib/ai/types";
import { useRouter } from "@/lib/navigation";
import { cn } from "@/lib/utils";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import { DELETE_SEARCH_CONSOLE_PROPERTY_NAME } from "./constants";
import type { DeleteSearchConsolePropertyInput } from "./schema";

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

const RegisteredPropertiesQuery = graphql(`
  query RegisteredPropertiesForDeleteScPropertyTool($workspaceId: ID!) {
    organization: node(id: $workspaceId) {
      ... on Organization {
        id
        __typename
        registeredProperties {
          __typename
          ... on RegisteredSearchConsoleUserProperty {
            id
            property
            user {
              email
            }
            indexWorkers {
              id
            }
          }
          ... on RegisteredSearchConsoleServiceAccountProperty {
            id
            property
            serviceAccount {
              email
            }
            indexWorkers {
              id
            }
          }
        }
      }
    }
  }
`);

type RegisteredPropertyOption = {
  id: string;
  property: string;
  ownerEmail: string;
  indexWorkerCount: number;
};

export function DeleteSearchConsolePropertyConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const { t } = useLingui();
  const router = useRouter();
  const params = useParams();
  const workspaceIdFromParams =
    typeof params.workspaceId === "string"
      ? decodeURIComponent(params.workspaceId)
      : "";

  const toolName = isToolUIPart(part) ? getToolName(part) : null;
  const isTarget = toolName === DELETE_SEARCH_CONSOLE_PROPERTY_NAME;
  const isInputAvailable = part.state === "input-available";
  const input = (part.input ?? {}) as Partial<DeleteSearchConsolePropertyInput>;

  const [{ data, fetching, error }] = useQuery({
    query: RegisteredPropertiesQuery,
    variables: { workspaceId: workspaceIdFromParams },
    requestPolicy: "network-only",
    pause: !(isTarget && isInputAvailable && workspaceIdFromParams),
  });

  const options = React.useMemo<RegisteredPropertyOption[]>(() => {
    if (data?.organization?.__typename !== "Organization") {
      return [];
    }

    return data.organization.registeredProperties
      .map((property) => {
        if (property.__typename === "RegisteredSearchConsoleUserProperty") {
          return {
            id: String(property.id),
            property: property.property,
            ownerEmail: property.user.email,
            indexWorkerCount: property.indexWorkers.length,
          };
        }
        if (
          property.__typename ===
          "RegisteredSearchConsoleServiceAccountProperty"
        ) {
          return {
            id: String(property.id),
            property: property.property,
            ownerEmail: property.serviceAccount.email,
            indexWorkerCount: property.indexWorkers.length,
          };
        }
        return null;
      })
      .filter((property): property is RegisteredPropertyOption => !!property);
  }, [data]);

  const [selectedPropertyId, setSelectedPropertyId] = React.useState<string>(
    input.propertyId ?? "",
  );

  React.useEffect(() => {
    setSelectedPropertyId(input.propertyId ?? "");
  }, [input.propertyId]);

  const selected = React.useMemo(
    () => options.find((option) => option.id === selectedPropertyId),
    [options, selectedPropertyId],
  );

  if (!isTarget) return null;

  if (part.state === "output-available") {
    const output = part.output as
      | {
          ok?: boolean;
          id?: string;
          property?: string;
          settingsPath?: string;
          settingsUrl?: string;
        }
      | undefined;

    if (output?.ok) {
      return (
        <div className="rounded-md border bg-muted/20 p-3 text-sm space-y-2">
          <p>
            <Trans>Property deleted:</Trans>{" "}
            <strong>{output.property ?? output.id ?? "-"}</strong>
          </p>
          {output.settingsUrl ? (
            <a
              href={output.settingsUrl}
              target="_blank"
              rel="noreferrer"
              className="text-primary underline"
            >
              <Trans>Open Search Console properties settings</Trans>
            </a>
          ) : null}
        </div>
      );
    }
  }

  if (!isInputAvailable) return null;

  const canApprove =
    !!workspaceIdFromParams &&
    !!selected &&
    !fetching &&
    selected.indexWorkerCount === 0;

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="space-y-1">
        <h4 className="font-medium">
          <Trans>Delete Search Console property</Trans>
        </h4>
        <p className="text-sm text-muted-foreground">
          <Trans>Select the property to delete from this workspace.</Trans>
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="delete-sc-property">
          <Trans>Property</Trans>
        </Label>
        <Popover modal={true}>
          <PopoverTrigger asChild>
            <Button
              id="delete-sc-property"
              variant="outline"
              role="combobox"
              className="w-full justify-between"
              disabled={fetching || options.length === 0}
            >
              <span className="truncate text-left">
                {selected ? (
                  <span>
                    {selected.property}
                    {selected.ownerEmail ? ` (${selected.ownerEmail})` : ""}
                  </span>
                ) : (
                  <span className="text-muted-foreground">
                    <Trans>Select property</Trans>
                  </span>
                )}
              </span>
              <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
          >
            <Command>
              <CommandInput placeholder={t`Search property`} className="h-9" />
              <CommandList>
                <CommandEmpty>
                  <Trans>No property found.</Trans>
                </CommandEmpty>
                <CommandGroup>
                  {options.map((option) => (
                    <CommandItem
                      key={option.id}
                      value={`${option.property} ${option.ownerEmail}`}
                      onSelect={() => setSelectedPropertyId(option.id)}
                    >
                      <div className="flex flex-col flex-1">
                        <span>{option.property}</span>
                        {option.ownerEmail ? (
                          <span className="text-xs text-muted-foreground">
                            {option.ownerEmail}
                          </span>
                        ) : null}
                      </div>
                      <CheckIcon
                        className={cn(
                          "ml-auto h-4 w-4",
                          selectedPropertyId === option.id
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {selected && selected.indexWorkerCount > 0 ? (
        <Alert variant="destructive">
          <AlertCircleIcon className="h-4 w-4" />
          <AlertTitle>
            <Trans>Property cannot be deleted</Trans>
          </AlertTitle>
          <AlertDescription>
            <Trans>
              This property is used by {selected.indexWorkerCount} Index
              Worker(s). Update or delete those Index Workers if needed.
            </Trans>
          </AlertDescription>
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="destructive">
          <AlertCircleIcon className="h-4 w-4" />
          <AlertTitle>
            <Trans>Error</Trans>
          </AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      ) : null}

      <div className="flex items-center justify-end gap-2">
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={toolName ?? DELETE_SEARCH_CONSOLE_PROPERTY_NAME}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
        {canApprove ? (
          <ApproveButton
            toolCallId={part.toolCallId}
            toolName={toolName ?? DELETE_SEARCH_CONSOLE_PROPERTY_NAME}
            addToolResult={addToolResult}
            sendMessage={sendMessage}
            variant="destructive"
            onSuccess={() => {
              router.refresh();
            }}
            overrideInput={{
              workspaceId: workspaceIdFromParams,
              propertyId: selectedPropertyId,
            }}
          >
            <Trans>Delete</Trans>
          </ApproveButton>
        ) : (
          <Button size="sm" variant="destructive" disabled>
            <Trans>Delete</Trans>
          </Button>
        )}
      </div>
    </div>
  );
}
